import { Navigate, Link as RouterLink, useNavigate } from 'react-router-dom'
import { useState, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { routes } from '@/router'
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import logoGoogle from '@/assets/images/logo-google.svg'
import { LoginFormData, loginFormSchema } from './form.shema'
import { useCreateSession } from '@/api/user-manager'
import { LoadingButton } from '@mui/lab'
import { useGoogleLogin } from '@react-oauth/google'
import { useCreateGoogleSession } from '@/api/user-manager'
import { usePlatform } from '@/hooks'
import { getPendingInvite, pendingInvitePath } from '@/helpers'

export function SignInPage() {
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const passwordInputRef = useRef<HTMLInputElement>(null)
  const { platform } = usePlatform()

  const accessToken = localStorage.getItem('access_token')
  const pendingInvite = getPendingInvite()
  const afterLoginPath = pendingInvite
    ? pendingInvitePath(pendingInvite)
    : routes.home

  const handleTogglePasswordVisibility = () => {
    if (!passwordInputRef.current) return

    const currentPosition = passwordInputRef.current.selectionStart || 0
    setShowPassword(!showPassword)

    requestAnimationFrame(() => {
      if (passwordInputRef.current) {
        passwordInputRef.current.setSelectionRange(
          currentPosition,
          currentPosition,
        )
        passwordInputRef.current.focus()
      }
    })
  }

  const { handleSubmit, control } = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const { mutateAsync: createGoogleSession } = useCreateGoogleSession({
    onSuccess({ access_token }) {
      localStorage.setItem('access_token', access_token)
      navigate(afterLoginPath)
    },
  })

  const login = useGoogleLogin({
    onSuccess: async (response) => {
      const { access_token } = response
      createGoogleSession({
        access_token,
      })
    },
  })

  const { mutateAsync: createSession, isPending } = useCreateSession({
    onSuccess({ access_token }) {
      localStorage.setItem('access_token', access_token)
      navigate(afterLoginPath)
    },
  })

  // ensure user is in home page if has access token
  if (accessToken) {
    return <Navigate to={afterLoginPath} replace />
  }

  function handleLogin({ email, password }: LoginFormData) {
    createSession({ alias: email.toLowerCase(), alias_type: 'email', password })
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'auto',
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(handleLogin)}
        maxWidth={360}
        display="flex"
        flexDirection="column"
        gap={5}
      >
        <Stack spacing={2}>
          <Typography variant="h2" style={{ fontWeight: 300, lineHeight: 1.4 }}>
            Seja bem-vindo(a) à <br />
            <strong>Alume Contabilidade</strong>
          </Typography>
          <Typography variant="body1">
            Acesse sua conta para emitir notas fiscais, controlar seus impostos
            e muito mais.
          </Typography>
          {pendingInvite && (
            <Alert severity="info">
              Entre com sua conta para aceitar o convite.
            </Alert>
          )}
        </Stack>

        <Stack spacing={2}>
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                label="E-mail"
                error={Boolean(error)}
                helperText={error?.message}
                slotProps={{
                  input: {
                    autoCapitalize: 'none',
                    style: { textTransform: 'lowercase' },
                  },
                }}
                {...field}
                onChange={(e) => field.onChange(e.target.value.toLowerCase())}
              />
            )}
          />

          <Controller
            name="password"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                label="Senha"
                type={showPassword ? 'text' : 'password'}
                helperText={
                  error?.message || (
                    <Link
                      component={RouterLink}
                      to={routes.session.passwordRecovery}
                    >
                      Esqueceu sua senha?
                    </Link>
                  )
                }
                error={Boolean(error)}
                inputRef={passwordInputRef}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleTogglePasswordVisibility}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                {...field}
              />
            )}
          />
        </Stack>

        <Stack spacing={2}>
          <LoadingButton
            type="submit"
            size="large"
            fullWidth
            loading={isPending}
          >
            Entrar
          </LoadingButton>

          {platform === 'web' && (
            <Button
              variant="outlined"
              size="large"
              fullWidth
              onClick={() => login()}
              startIcon={
                <Box
                  component="img"
                  src={logoGoogle}
                  alt="Logo da Google"
                  width={20}
                />
              }
            >
              Entrar com o Google
            </Button>
          )}

          <Button
            variant="outlined"
            size="large"
            fullWidth
            component={RouterLink}
            to={routes.session.signup}
          >
            Ainda não sou cliente
          </Button>
        </Stack>
        <Stack pb={2}>
          <Typography variant="body2" align="center">
            Ao seguir, você está de acordo com os{' '}
            <Link
              component={RouterLink}
              target="_blank"
              to={'https://app.alume.com/use-terms'}
            >
              Termos de uso
            </Link>{' '}
            e{' '}
            <Link
              component={RouterLink}
              target="_blank"
              to={'https://app.alume.com/privacy-policy'}
            >
              Aviso de Privacidade
            </Link>
          </Typography>
        </Stack>
      </Box>
    </Box>
  )
}
